// Tiny date formatting helpers. UK locale.
// All persistence stays ISO YYYY-MM-DD; these only format for display.

const PAD = (n) => String(n).padStart(2, "0");

/** ISO YYYY-MM-DD (or full ISO datetime) -> "DD-MM-YYYY". Returns "—" for falsy. */
export function fmtDate(iso) {
  if (!iso) return "—";
  // Take just the date portion if a full datetime is given
  const datePart = String(iso).slice(0, 10);
  const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** ISO YYYY-MM-DD -> "DD-MM-YY". */
export function fmtDateShort(iso) {
  const m = String(iso || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1].slice(2)}` : (iso || "—");
}

/** Local Date -> ISO YYYY-MM-DD (no UTC shift). */
export function localIso(d) {
  return `${d.getFullYear()}-${PAD(d.getMonth() + 1)}-${PAD(d.getDate())}`;
}

/** Hours number -> "1.50h". */
export const fmtHours = (h) => `${Number(h || 0).toFixed(2)}h`;

/** ISO datetime -> "DD-MM-YYYY HH:MM" */
export function fmtDateTime(iso) {
  if (!iso) return "—";
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return fmtDate(s);
  return `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}`;
}

/** Convert a "DD-MM-YYYY" string back to "YYYY-MM-DD" for sending to backend. */
export function ukToIso(uk) {
  if (!uk) return "";
  const m = String(uk).match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return uk;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Today as ISO YYYY-MM-DD (UTC-safe enough for client-side defaults). */
export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${PAD(d.getMonth() + 1)}-${PAD(d.getDate())}`;
}

/**
 * Iter 110m — Compact "X ago" relative-time string for at-a-glance
 * freshness captions. Buckets:
 *   <60s   → "just now"
 *   <60m   → "Nm ago"
 *   <24h   → "Nh ago"
 *   <7d    → "Nd ago"
 *   otherwise → "DD MMM" (or "DD MMM YYYY" if not this year)
 *
 * Returns "—" for falsy. Always renders in the local timezone.
 */
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function fmtRelative(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!t) return "—";
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return sameYear
    ? `${PAD(d.getDate())} ${SHORT_MONTHS[d.getMonth()]}`
    : `${PAD(d.getDate())} ${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
